// ============================================
// In-App Purchase Module (ES6)
// cordova-plugin-purchase 기반 결제 처리
// ============================================

// cordova-plugin-purchase v13+ 필수 import
import 'cordova-plugin-purchase';

import { showToast } from '../utils.js';
import { PAYMENT_ENDPOINTS } from '../shared/api-endpoints.js';
import { authFetch } from '../shared/auth-fetch.js';
import { getCurrentUser } from '../app/state.js';
import { getUserCarbon, setUserCarbon } from '../auth/state.js';

// ============================================
// State
// ============================================

let isInitialized = false;
let registeredProducts = [];
let serverProducts = []; // 서버에서 로드한 원본 상품 목록

// ============================================
// Initialize IAP
// ============================================

/**
 * IAP 스토어 초기화
 * - Capacitor 네이티브 앱에서만 실행
 * - cordova-plugin-purchase (CdvPurchase) 사용
 */
export async function initializeIAP() {
  // 웹에서는 초기화하지 않음
  if (!window.Capacitor?.isNativePlatform?.()) {
    console.log('[IAP] Web platform - skipping initialization');
    return;
  }

  // 이미 초기화됨
  if (isInitialized) {
    console.log('[IAP] Already initialized');
    return;
  }

  // CdvPurchase가 로드될 때까지 대기
  if (!window.CdvPurchase) {
    console.log('[IAP] Waiting for CdvPurchase to load...');
    await waitForCdvPurchase();
  }

  if (!window.CdvPurchase) {
    console.error('[IAP] CdvPurchase not available');
    return;
  }

  try {
    const { store, Platform, ProductType, LogLevel } = window.CdvPurchase;

    // 디버그 로깅 (개발 중에만)
    store.verbosity = LogLevel.DEBUG;

    // 에러 핸들러
    store.error((error) => {
      console.error('[IAP] Store error:', error);
    });

    // 서버에서 상품 목록 로드
    const products = await loadProductsFromServer();
    if (!products || products.length === 0) {
      console.warn('[IAP] No products to register');
      return;
    }
    serverProducts = products;

    // 플랫폼 결정
    const platform = window.Capacitor.getPlatform() === 'ios'
      ? Platform.APPLE_APPSTORE
      : Platform.GOOGLE_PLAY;

    // 상품 등록
    for (const product of products) {
      const productId = platform === Platform.APPLE_APPSTORE
        ? product.appleProductId
        : product.googleProductId;

      if (productId && typeof productId === 'string' && productId.trim() !== '') {
        try {
          store.register({
            id: productId,
            type: ProductType.CONSUMABLE,
            platform: platform
          });
          registeredProducts.push({ ...product, storeProductId: productId });
          console.log('[IAP] Registered product:', productId);
        } catch (regError) {
          console.error('[IAP] 상품 등록 실패:', productId, regError);
        }
      }
    }

    // 이벤트 핸들러 등록 (초기화 전에 등록해야 함)
    store.when()
      .productUpdated(handleProductUpdated)
      .approved(handleApproved)
      .verified(handleVerified)
      .finished(handleFinished)
      .unverified(handleUnverified);

    // 스토어 초기화
    await store.initialize([platform]);
    console.log('[IAP] Store initialized');

    // 상품 정보 로드
    await store.update();
    console.log('[IAP] Products updated');

    isInitialized = true;
    console.log('[IAP] Initialization complete');

  } catch (error) {
    console.error('[IAP] Initialization failed:', error);
  }
}

// ============================================
// Event Handlers
// ============================================

/**
 * 상품 정보 업데이트됨
 */
function handleProductUpdated(product) {
  console.log('[IAP] Product updated:', product.id, 'state:', product.state, 'canPurchase:', product.canPurchase);
}

/**
 * 구매 승인됨 - 서버 검증 시작
 */
async function handleApproved(transaction) {
  console.log('[IAP] Purchase approved:', transaction);

  try {
    const product = registeredProducts.find(p => p.storeProductId === transaction.products[0]?.id);
    if (!product) {
      console.error('[IAP] Product not found for transaction');
      return;
    }

    const platform = window.Capacitor.getPlatform();

    if (platform === 'android') {
      // Google Play 검증
      const verified = await verifyGooglePurchase(
        transaction.products[0].id,
        transaction.purchaseToken || transaction.transactionId,
        transaction.transactionId
      );

      if (verified) {
        transaction.verify();
      } else {
        console.error('[IAP] Google verification failed');
        showToast('결제 검증에 실패했습니다');
      }
    } else if (platform === 'ios') {
      // Apple 검증
      const verified = await verifyApplePurchase(
        transaction.products[0].id,
        transaction.appStoreReceipt,
        transaction.transactionId
      );

      if (verified) {
        transaction.verify();
      } else {
        console.error('[IAP] Apple verification failed');
        showToast('결제 검증에 실패했습니다');
      }
    }
  } catch (error) {
    console.error('[IAP] Verification error:', error);
    showToast('결제 처리 중 오류가 발생했습니다');
  }
}

/**
 * 검증 완료 - 구매 완료 처리
 */
function handleVerified(receipt) {
  console.log('[IAP] Receipt verified:', receipt);
  receipt.finish();
}

/**
 * 구매 완료
 */
function handleFinished(transaction) {
  console.log('[IAP] Transaction finished:', transaction);
}

/**
 * 검증 실패
 */
function handleUnverified(receipt) {
  console.error('[IAP] Receipt unverified:', receipt);
  showToast('결제 검증에 실패했습니다');
}

// ============================================
// Server Verification
// ============================================

/**
 * Google Play 구매 검증
 */
async function verifyGooglePurchase(productId, purchaseToken, orderId) {
  try {
    const response = await authFetch(PAYMENT_ENDPOINTS.verifyGoogle, {
      method: 'POST',
      body: { productId, purchaseToken, orderId }
    });

    const result = await response.json();

    if (result.success) {
      showToast(result.message || '충전 완료!');
      updateLocalCarbon(result.carbonGranted);
      // 결제 완료 이벤트 발생 - 모달 닫기 및 UI 업데이트 트리거
      dispatchPurchaseCompleted(result.carbonGranted);
      return true;
    } else {
      showToast(result.error?.message || '결제 검증에 실패했습니다');
      return false;
    }
  } catch (error) {
    console.error('[IAP] Google verification error:', error);
    return false;
  }
}

/**
 * Apple App Store 구매 검증
 */
async function verifyApplePurchase(productId, receiptData, transactionId) {
  try {
    const response = await authFetch(PAYMENT_ENDPOINTS.verifyApple, {
      method: 'POST',
      body: { productId, receiptData, transactionId }
    });

    const result = await response.json();

    if (result.success) {
      showToast(result.message || '충전 완료!');
      updateLocalCarbon(result.carbonGranted);
      // 결제 완료 이벤트 발생 - 모달 닫기 및 UI 업데이트 트리거
      dispatchPurchaseCompleted(result.carbonGranted);
      return true;
    } else {
      showToast(result.error?.message || '결제 검증에 실패했습니다');
      return false;
    }
  } catch (error) {
    console.error('[IAP] Apple verification error:', error);
    return false;
  }
}

// ============================================
// Helpers
// ============================================

/**
 * CdvPurchase 로드 대기
 */
function waitForCdvPurchase(timeout = 5000) {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const check = () => {
      if (window.CdvPurchase) {
        resolve(true);
      } else if (Date.now() - startTime > timeout) {
        resolve(false);
      } else {
        setTimeout(check, 100);
      }
    };

    check();
  });
}

/**
 * 서버에서 상품 목록 로드
 */
async function loadProductsFromServer() {
  try {
    const response = await fetch(PAYMENT_ENDPOINTS.getProducts, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });

    if (!response.ok) {
      throw new Error('Failed to load products');
    }

    const result = await response.json();
    return result.success ? result.products : [];
  } catch (error) {
    console.error('[IAP] Failed to load products:', error);
    return [];
  }
}

/**
 * 로컬 카본 잔액 업데이트
 */
function updateLocalCarbon(amount) {
  const user = getCurrentUser();
  if (user && amount) {
    const currentCarbon = getUserCarbon() || 0;
    setUserCarbon(currentCarbon + amount);
  }
}

/**
 * 결제 완료 이벤트 발생
 */
function dispatchPurchaseCompleted(carbonAmount) {
  const event = new CustomEvent('purchaseCompleted', {
    detail: { carbonAmount }
  });
  window.dispatchEvent(event);
}

/**
 * 상품 구매 시작
 * @param {string} productId - 서버 상품 ID
 */
export async function purchaseProduct(productId) {
  if (!isInitialized) {
    showToast('결제 모듈을 초기화하는 중입니다');
    return;
  }

  const product = registeredProducts.find(p => p.id === productId);
  if (!product) {
    showToast('상품 정보를 찾을 수 없습니다');
    return;
  }

  try {
    const { store } = window.CdvPurchase;

    // store.products에서 직접 찾기
    const storeProduct = store.products.find(p => p.id === product.storeProductId);

    if (!storeProduct) {
      showToast('스토어에서 상품을 찾을 수 없습니다');
      return;
    }

    // v13에서는 Product가 아닌 Offer를 주문해야 함
    const offers = storeProduct.offers || [];
    if (offers.length === 0) {
      showToast('구매 가능한 옵션이 없습니다');
      return;
    }

    const offer = offers[0]; // 첫 번째 offer 사용
    showToast('결제를 진행합니다...');
    await store.order(offer);
  } catch (error) {
    console.error('[IAP] Purchase error:', error);
    showToast('결제에 실패했습니다');
  }
}

/**
 * 미완료 구매 복원
 */
export async function restorePurchases() {
  if (!isInitialized || !window.CdvPurchase) {
    return;
  }

  try {
    await window.CdvPurchase.store.restorePurchases();
    console.log('[IAP] Purchases restored');
  } catch (error) {
    console.error('[IAP] Restore failed:', error);
  }
}

// ============================================
// Getters
// ============================================

/**
 * IAP 초기화 여부 확인
 */
export function isIAPInitialized() {
  return isInitialized;
}

/**
 * 서버에서 로드한 상품 목록 반환
 */
export function getServerProducts() {
  return serverProducts;
}

/**
 * 등록된 상품 목록 반환
 */
export function getRegisteredProducts() {
  return registeredProducts;
}

// ============================================
// Exports
// ============================================

export const IAPModule = {
  initializeIAP,
  purchaseProduct,
  restorePurchases,
  isIAPInitialized,
  getServerProducts,
  getRegisteredProducts
};
